import { getPollingIntervalMsFromLocalStorage } from 'helpers/browser';
import { handleError } from 'globals/errors';
import { Component } from 'react';
import { AppProps, AppState } from './types';

export class Polling {
  static pollIntervalTimer: NodeJS.Timer | undefined;

  /**
   * Initiates polling for new messages in active conversations at a specified interval.
   *
   * Behavior:
   * - If no new messages are received, the polling interval increases (backoff).
   * - If new messages are received, polling resets to the original interval.
   * - If an error occurs while fetching messages, the interval increases (backoff).
   *
   * @param {number} interval - The current polling interval in milliseconds.
   * @param {Component<AppProps, AppState>} component - The React component managing polling state.
   * @param {() => Promise<void>[]} fetchPromisesFn - A function returning an array of promises for fetching messages.
   */
  static poll = (
    interval: number,
    component: Component<AppProps, AppState>,
    fetchPromisesFn: () => Promise<void>[],
  ) => {
    const oldMessageCount = component.state.polledMessageCount;
    component.setState({ polledMessageCount: 0 }, () => {
      // wait for all fetch operations to complete
      Promise.all(fetchPromisesFn())
        .then(() =>
          this.backoffOrRestartPolling(
            oldMessageCount || 0,
            interval,
            component,
            fetchPromisesFn,
          ),
        )
        .catch((e) => {
          handleError(e, {
            message: 'initApp Message Poller: Error fetching message history',
          });
          // if we have an error, backoff polling
          this.backoffPolling(interval, component, fetchPromisesFn);
        });
    });
  };

  /**
   * Determines whether to continue polling at the same interval, increase the polling interval (backoff),
   * or reset to the original interval based on the number of new messages received.
   *
   * Behavior:
   * - If no messages have ever been fetched (`oldMessageCount === 0` and `polledMessageCount === 0`),
   *   the function does nothing, allowing the current interval to continue.
   * - If the number of messages received (`polledMessageCount`) is the same as before (`oldMessageCount`),
   *   it triggers **backoffPolling**, increasing the polling interval.
   * - If new messages have been received (`polledMessageCount` > `oldMessageCount`),
   *   it resets polling to the original interval via **restartPolling**.
   *
   * @param {number} oldMessageCount - The previous count of polled messages.
   * @param {number} interval - The current polling interval in milliseconds.
   * @param {Component<AppProps, AppState>} component - The React component managing polling state.
   * @param {() => Promise<void>[]} fetchPromisesFn - A function returning an array of promises for fetching messages.
   */
  static backoffOrRestartPolling = (
    oldMessageCount: number,
    interval: number,
    component: Component<AppProps, AppState>,
    fetchPromisesFn: () => Promise<void>[],
  ) => {
    if (component.state.polledMessageCount === 0 && oldMessageCount === 0) {
      // when nothing is fetched yet, do nothing
      // allow timer to run at same interval again
      return;
    }
    // if we have not received any new messages, backoff polling
    if (component.state.polledMessageCount === oldMessageCount) {
      this.backoffPolling(interval, component, fetchPromisesFn);
    } else {
      // if we have received new messages, restart polling back to original interval
      this.restartPolling(component, fetchPromisesFn);
    }
  };

  /**
   * Stops the polling mechanism by clearing the existing interval timer.
   * This prevents further polling until it is restarted.
   */
  static clearPolling = () => {
    if (this.pollIntervalTimer) {
      global.clearInterval(this.pollIntervalTimer);
      this.pollIntervalTimer = undefined;
    }
  };

  /**
   * Increases the polling interval (backoff) by doubling it, up to a maximum of 120 minutes (7200000 ms).
   * If polling is still needed, a new interval is set with the updated time.
   *
   * @param {number} interval - The current polling interval in milliseconds.
   * @param {Component<AppProps, AppState>} component - The React component managing polling state.
   * @param {() => Promise<void>[]} fetchPromisesFn - A function returning an array of promises for fetching messages.
   */
  static backoffPolling = (
    interval: number,
    component: Component<AppProps, AppState>,
    fetchPromisesFn: () => Promise<void>[],
  ) => {
    this.clearPolling();
    // Multiply interval by 2 but cap it at 120 minutes
    const newInterval = Math.min(interval * 2, 7200000);
    this.pollIntervalTimer = global.setInterval(() => {
      this.poll(newInterval, component, fetchPromisesFn);
    }, newInterval);
  };

  /**
   * Restarts the polling mechanism using the polling interval stored in local storage.
   * If polling is enabled (interval > 0), it sets a new polling interval.
   *
   * @param {Component<AppProps, AppState>} component - The React component managing polling state.
   * @param {() => Promise<void>[]} fetchPromisesFn - A function returning an array of promises for fetching messages.
   */
  static restartPolling = (
    component: Component<AppProps, AppState>,
    fetchPromisesFn: () => Promise<void>[],
  ) => {
    this.clearPolling();
    const pollingIntervalMs = getPollingIntervalMsFromLocalStorage();
    if (pollingIntervalMs > 0) {
      // 0 indicates polling is turned off, so skip if 0
      this.pollIntervalTimer = global.setInterval(() => {
        this.poll(pollingIntervalMs, component, fetchPromisesFn);
      }, pollingIntervalMs);
    }
  };
}
